import { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import ModelAutoComplete from "../../components/ModelAutoComplete";
import ApiAutoComplete from "../../components/ApiAutoComplete";
import RadioGroup from "../../components/RadioGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { filterTypes } from "../../constants/ui";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { standardDateFormat } from "../../utils/calendar";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { capitalize } from "../../utils/formatting";
import { FormControlLabel, Switch } from "@mui/material";
import dayjs from "dayjs";

function Filter({ value, type, props, onChange }) {
  const inputProps = props.InputProps || {};
  const [loadedOptions, setLoadedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadOptions = props.loadOptions;

  value = value ?? props.default;

  useEffect(() => {
    if (type === filterTypes.LOAD_OPTIONS && loadOptions) {
      setLoading(true);
      loadOptions()
        .then((options) => {
          setLoadedOptions(options);
        })
        .catch((error) => {
          console.error("Error loading filter options:", error);
          setLoadedOptions([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [type, loadOptions]);

  if (type === filterTypes.MODEL_AUTOCOMPLETE) {
    return (
      <ModelAutoComplete
        {...props}
        {...inputProps}
        value={value}
        setValue={(newValue) => onChange(newValue)}
      />
    );
  } else if (type === filterTypes.API_AUTOCOMPLETE) {
    return (
      <ApiAutoComplete
        {...props}
        {...inputProps}
        value={value}
        onChange={(newValue) => onChange(newValue)}
      />
    );
  } else if (type === filterTypes.RADIO_GROUP) {
    return (
      <RadioGroup
        {...props}
        {...inputProps}
        value={value}
        setValue={(newValue) => onChange(newValue)}
      />
    );
  } else if (type === filterTypes.SELECT) {
    return (
      <FormControl fullWidth variant="standard">
        <InputLabel>{props.label}</InputLabel>
        <Select
          value={value || ""}
          label={props.label}
          onChange={(event) => onChange(event.target.value)}
          variant="standard"
          inputProps={inputProps}
        >
          {props.options.map((option, i) => (
            <MenuItem key={i} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  } else if (type === filterTypes.LOAD_OPTIONS) {
    return (
      <FormControl fullWidth variant="standard">
        <InputLabel>{props.label}</InputLabel>
        <Select
          value={value || ""}
          label={props.label}
          onChange={(event) => onChange(event.target.value)}
          variant="standard"
          inputProps={inputProps}
          disabled={loading}
        >
          {loading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : (
            loadedOptions.map((option, i) => (
              <MenuItem key={i} value={option.value}>
                {option.label}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    );
  } else if (type === filterTypes.DATE_PICKER) {
    value = typeof value == "string" ? dayjs(value) : value;
    return (
      <DatePicker
        label={props.label}
        value={value}
        onChange={(newValue) => onChange(newValue)}
        slotProps={{
          textField: { variant: "standard", fullWidth: true, ...inputProps },
          field: {
            clearable: true,
            onClear: () => onChange(null),
          },
        }}
        {...(props.DatePickerProps ?? {})}
      />
    );
  } else if (type === filterTypes.SWITCH) {
    return (
      <FormControlLabel
        control={
          <Switch
            onChange={(event, checked) => onChange(checked)}
            checked={value}
            color="secondary"
          />
        }
        label={props.label}
      />
    );
  } else {
    throw Error(`Invalid filter type provided: ${type}`);
  }
}
function FiltersContainer({ filtersConfig, filters, setFilters, handleClose }) {
  const handleUpdateFilter = (key, val) => {
    setFilters((filters) => ({
      ...filters,
      [key]: val,
    }));
  };

  function groupFilters(filtersConfig) {
    const groups = new Set(
      filtersConfig.map((filterConfig) =>
        filterConfig.group ? filterConfig.group : "empty"
      )
    );
    const groupsConfig = Array.from(groups).map((group) => {
      const groupFilters = filtersConfig.filter((filterConfig) => {
        if (filterConfig.group) {
          return filterConfig.group == group;
        }
        return group == "empty";
      });
      return {
        name: group,
        filtersConfig: groupFilters,
      };
    });
    return groupsConfig;
  }

  return (
    <Box
      sx={{
        width: "100%",
        padding: "0 1rem",
        marginBottom: "10rem",
      }}
    >
      {handleClose && (
        <>
          <IconButton onClick={handleClose} size="small">
            <ChevronRight />
          </IconButton>
          <Divider />
        </>
      )}
      <Stack
        direction={"row"}
        alignItems={"center"}
        sx={{
          marginTop: ".5rem",
        }}
      >
        <Typography variant="overline" fontWeight={"bold"} fontSize={"large"}>
          Filters
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {groupFilters(filtersConfig).map((group, i) => (
          <Grid container key={i} size={12}>
            {group.name != "empty" && (
              <Grid size={12}>
                <Divider sx={{ mt: 1 }}>
                  <Typography
                    variant="body1"
                    fontSize={"large"}
                    fontWeight={"bold"}
                  >
                    {capitalize(group.name)}
                  </Typography>
                </Divider>
              </Grid>
            )}
            {group.filtersConfig.map((filterConf, i) => (
              <Grid size={filterConf.gridSize || 12} key={i}>
                <Filter
                  {...filterConf}
                  value={filters[filterConf.field]}
                  onChange={(newValue) =>
                    handleUpdateFilter(filterConf.field, newValue)
                  }
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function getFilterValue(filterConfig, filters) {
  const { type, field } = filterConfig;
  const value = filters[field] ?? filterConfig.props?.default;
  if (value === undefined || value === "" || value === null) {
    return null;
  }
  if (type === filterTypes.MODEL_AUTOCOMPLETE) {
    if (Array.isArray(value)) {
      if (!value.length) {
        return null;
      }
      return [field, value.map((val) => val.uuid)];
    }
    return [field, value.uuid];
  }
  if (type === filterTypes.API_AUTOCOMPLETE) {
    if (Array.isArray(value)) {
      if (!value.length) {
        return null;
      }
      return [field, value.map((val) => val.value)];
    }
    return [field, value.value];
  }
  if (type === filterTypes.DATE_PICKER) {
    if (typeof value == "string") {
      return [field, value.split("T")[0]];
    }
    return [field, standardDateFormat(value.toDate())];
  }
  if (type === filterTypes.SWITCH && filterConfig.props?.opposite) {
    return [field, !value];
  }

  return [field, value];
}

function getFilterLabel(filterConfig, filters) {
  const { type, field } = filterConfig;
  const value = filters[field];
  if (value === undefined || value === "" || value === null) {
    return null;
  }
  if (Array.isArray(value) && !value.length) {
    return;
  }

  if (type === filterTypes.MODEL_AUTOCOMPLETE) {
    let label = `${filterConfig.props.label}: `;
    if (Array.isArray(value)) {
      label += value.map((val) => val.label).join(", ");
    } else {
      label += value.label;
    }
    return label;
  } else if (type === filterTypes.API_AUTOCOMPLETE) {
    let label = `${filterConfig.props.label}: `;
    if (Array.isArray(value)) {
      label += value.map((val) => val.label).join(", ");
    } else {
      label += value.label;
    }
    return label;
  } else if (
    type === filterTypes.RADIO_GROUP ||
    type === filterTypes.SELECT ||
    type === filterTypes.LOAD_OPTIONS
  ) {
    // For LOAD_OPTIONS, we need to handle the case where options might not be loaded yet
    const options =
      type === filterTypes.LOAD_OPTIONS
        ? filterConfig.cachedOptions || []
        : filterConfig.props.options;

    const optionLabel = options.find(
      (option) => option.value.toString() === value.toString()
    );

    if (optionLabel) {
      return `${filterConfig.props.label}: ${optionLabel.label}`;
    }
    // Fallback if options aren't loaded yet
    return `${filterConfig.props.label}: ${value}`;
  } else if (type === filterTypes.DATE_PICKER) {
    const dateString =
      typeof value == "string"
        ? value.split("T")[0]
        : standardDateFormat(value.toDate());
    return `${filterConfig.props.label}: ${dateString}`;
  } else if (type === filterTypes.SWITCH) {
    return (
      <Typography component={"span"} display={"flex"} alignItems={"center"}>
        {filterConfig.props.label}: {value ? "Yes" : "No"}
      </Typography>
    );
  }

  return null;
}

export default function useFilters({
  name,
  filtersConfig,
  handleClose,
  ChipProps,
  saveState = true,
}) {
  if (saveState && !name) {
    throw new Error(
      `useFilters: 'name' prop is required to save filter state.`
    );
  }
  const initialState =
    saveState && name
      ? JSON.parse(localStorage.getItem(`${name}__FilterState`)) ?? {}
      : {};
  const [filters, setFilters] = useState(initialState);
  const FilterContainerComplete = useCallback(() => {
    return (
      <FiltersContainer
        filtersConfig={filtersConfig}
        filters={filters}
        setFilters={setFilters}
        handleClose={handleClose}
      />
    );
  }, [filtersConfig, filters, handleClose]);

  const handleDeleteFilter = (field) => {
    setFilters((filters) =>
      Object.fromEntries(
        Object.entries(filters).filter(([key]) => key !== field)
      )
    );
  };

  const FiltersAppliedList = useCallback(() => {
    const filtersCopy = { ...filters };
    for (let filterConfig of filtersConfig) {
      if (filtersCopy[filterConfig.field] != undefined) {
        continue;
      }
      if (filterConfig.props?.default) {
        filtersCopy[filterConfig.field] = filterConfig.props?.default;
      }
    }
    if (!Object.keys(filtersCopy).length) {
      return;
    }
    return (
      <Stack
        direction="row"
        flexWrap={"wrap"}
        sx={{
          padding: ".25rem",
        }}
      >
        {filtersConfig.map((filterConfig, i) => {
          const { field } = filterConfig;
          const label = getFilterLabel(filterConfig, filtersCopy);
          if (label == null) {
            return null;
          }

          return (
            <Chip
              key={i}
              label={label}
              onDelete={
                filterConfig.props?.default == undefined
                  ? () => handleDeleteFilter(field)
                  : undefined
              }
              sx={{
                margin: ".25rem",
              }}
              {...(ChipProps || {})}
            />
          );
        })}
      </Stack>
    );
  }, [filters, filtersConfig, ChipProps]);

  const getFilterParams = () => {
    return Object.fromEntries(
      filtersConfig
        .map((filterConfig) => getFilterValue(filterConfig, filters))
        .filter((val) => val != null)
    );
  };

  useEffect(() => {
    localStorage.setItem(`${name}__FilterState`, JSON.stringify(filters));
  }, [filters, name]);

  return {
    filters,
    getFilterParams,
    Filters: FilterContainerComplete,
    FiltersAppliedList,
  };
}
